import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Button } from '@heroui/react';
import type { LocaleCode } from '@/api/models';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { AccessNotice } from '../components';
import type { OperationsAccess } from '../operationsAccess';
import type { CategoryFormState, CategoryRow } from './categoriesMeta';
import {
    CategoryFormModal,
    CategoryTable,
    DeleteCategoryDialog,
} from './components';

type CategoriesPageViewProps = {
    access: OperationsAccess;
    canRead: boolean;
    locale: LocaleCode;
    isLoading: boolean;
    /** `null` while the first load is in flight. */
    rows: CategoryRow[] | null;
    onBack: () => void;

    form: CategoryFormState | null;
    /** `null` for a session that may not change anything. */
    onAdd: (() => void) | null;
    /** `null` for a session that may not change anything. */
    onRename: ((id: string) => void) | null;
    onFormNameKaChange: (value: string) => void;
    onFormNameEnChange: (value: string) => void;
    onFormCancel: () => void;
    onFormSubmit: () => void;
    isFormSubmittable: boolean;
    isSaving: boolean;
    formError: string | null;

    deleteTarget: CategoryRow | null;
    /** `null` for a session that may not change anything. */
    onDelete: ((id: string) => void) | null;
    onDeleteCancel: () => void;
    onDeleteConfirm: () => void;
    isDeleting: boolean;
    deleteError: string | null;
    blockingCount: number | null;
};

/**
 * Category management — render only. A back link, an intro, the list, and two
 * dialogs.
 *
 * The two footer notes are not decoration:
 *
 * - **A rename is safe and a removal is not the same as a block.** Renaming
 *   changes a label and nothing else — no entry reads differently, because a
 *   category is not a term in any recipe. That is worth saying beside a screen
 *   whose neighbour, block/unblock, changes what an operation may do.
 * - **A category in use cannot be removed.** Stated up front, that is a rule; met
 *   only as a refusal, it is a dead end. The way out — refile the operations from
 *   their own editors — is named with it, because there is no reassign-on-delete
 *   and there deliberately is not: refiling somebody's operations as a side
 *   effect of tidying a list changes what those operations say they are.
 */
export const CategoriesPageView: React.FC<CategoriesPageViewProps> = ({
    access,
    canRead,
    locale,
    isLoading,
    rows,
    onBack,
    form,
    onAdd,
    onRename,
    onFormNameKaChange,
    onFormNameEnChange,
    onFormCancel,
    onFormSubmit,
    isFormSubmittable,
    isSaving,
    formError,
    deleteTarget,
    onDelete,
    onDeleteCancel,
    onDeleteConfirm,
    isDeleting,
    deleteError,
    blockingCount,
}) => {
    const { t } = useTranslation('pages');

    if (!canRead) {
        return (
            <section
                aria-label={t('operations.categories.title')}
                className="flex flex-col gap-4"
                {...getNodeTestIdProp('operation-categories-section')}
            >
                <AccessNotice
                    access={
                        access as Exclude<OperationsAccess, { kind: 'full' }>
                    }
                />
            </section>
        );
    }

    return (
        <section
            aria-label={t('operations.categories.title')}
            className="flex flex-col gap-4"
            {...getNodeTestIdProp('operation-categories-section')}
        >
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="flex flex-col gap-2">
                    <Button
                        className="self-start"
                        onPress={onBack}
                        size="sm"
                        variant="ghost"
                        {...getNodeTestIdProp('operation-categories-back')}
                    >
                        <Icon aria-hidden icon="gravity-ui:arrow-left" />
                        {t('operations.categories.back')}
                    </Button>
                    <p className="max-w-prose text-sm text-muted">
                        {t('operations.categories.intro')}
                    </p>
                </div>

                {onAdd ? (
                    <Button
                        isDisabled={isLoading}
                        onPress={onAdd}
                        size="sm"
                        {...getNodeTestIdProp('operation-categories-add')}
                    >
                        <Icon aria-hidden icon="gravity-ui:plus" />
                        {t('operations.categories.actions.add')}
                    </Button>
                ) : null}
            </div>

            <CategoryTable
                ariaLabel={t('operations.categories.tableAria')}
                isLoading={isLoading}
                onDelete={onDelete}
                onRename={onRename}
                rows={rows ?? []}
            />

            <CategoryFormModal
                errorMessage={formError}
                form={form}
                isSaving={isSaving}
                isSubmittable={isFormSubmittable}
                locale={locale}
                onCancel={onFormCancel}
                onNameEnChange={onFormNameEnChange}
                onNameKaChange={onFormNameKaChange}
                onSubmit={onFormSubmit}
            />

            <DeleteCategoryDialog
                blockingCount={blockingCount}
                category={deleteTarget}
                errorMessage={deleteError}
                isPending={isDeleting}
                onCancel={onDeleteCancel}
                onConfirm={onDeleteConfirm}
            />

            <div className="flex flex-col gap-1">
                <p
                    className="text-xs text-muted"
                    {...getNodeTestIdProp('operation-categories-rename-note')}
                >
                    {t('operations.categories.footer.renameIsSafe')}
                </p>
                <p
                    className="text-xs text-muted"
                    {...getNodeTestIdProp('operation-categories-in-use-note')}
                >
                    {t('operations.categories.footer.inUse')}
                </p>
                {access.kind === 'readOnly' ? (
                    <p
                        className="text-xs text-muted"
                        {...getNodeTestIdProp(
                            'operation-categories-read-only-note',
                        )}
                    >
                        {t('operations.categories.footer.readOnly')}
                    </p>
                ) : null}
            </div>
        </section>
    );
};
